/** @format */

import "../Style/damtable.css";
import React, { useEffect, useMemo, useRef, useState } from "react";
import TooltipInfoBox from "./TooltipInfoBox";

export default function MonthlySummaryTable({
  damData,
  onTotalChange,
  STRIKE,
  QUANTITY,
}) {
  const [hasScroll, setHasScroll] = useState(false);

  const scrollRef = useRef(null);

  useEffect(() => {
    const checkScroll = () => {
      if (scrollRef.current) {
        setHasScroll(
          scrollRef.current.scrollHeight > scrollRef.current.clientHeight
        );
      }
    };
    checkScroll();
    const timeout = setTimeout(checkScroll, 300);

    window.addEventListener("resize", checkScroll);
    return () => {
      window.removeEventListener("resize", checkScroll);
      clearTimeout(timeout);
    };
  }, [damData, STRIKE, QUANTITY]);

  const monthlyData = useMemo(() => {
    if (!damData || damData.length === 0) return [];

    const grouped = {};

    damData.forEach((row) => {
      const d = new Date(row.date);
      const monthKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
        2,
        "0"
      )}`;

      const mcp = Number(row.avgMCP) || 0;

      if (!grouped[monthKey]) {
        grouped[monthKey] = { sum: 0, days: 0 };
      }

      grouped[monthKey].sum += mcp; // άθροισμα ημερήσιων avg MCP
      grouped[monthKey].days += 1; // πλήθος ημερών του μήνα
    });

    return Object.entries(grouped)
      .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
      .map(([monthKey, info]) => {
        const [year, month] = monthKey.split("-");
        const date = new Date(`${year}-${month}-01`);
        const formatter = new Intl.DateTimeFormat("en-US", { month: "long" });

        // Μέσος DAM μήνα (μέσος ημερήσιος = μέσος ωριαίος)
        const avgDamRaw = info.sum / Math.max(info.days, 1);
        const avgDam = Math.round(avgDamRaw * 100) / 100;

        // 🔹 ΥΠΟΛΟΓΙΣΜΟΣ ΩΡΩΝ ΜΗΝΑ
        let hoursInMonth = info.days * 24;

        // Προσθέτουμε extra ώρα για τον Οκτώβρη 2025 (DST +1)
        if (year === "2025" && month === "10") {
          hoursInMonth += 1;
        }

        // (avg(DAM)_μήνα - STRIKE) * QUANTITY * ώρες_μήνα
        const totalRaw = (avgDam - STRIKE) * QUANTITY * hoursInMonth;
        const total = Math.round(totalRaw * 100) / 100; // στρογγυλοποίηση 2 δεκαδικά

        return {
          year,
          month: formatter.format(date),
          avgDam,
          total,
        };
      });
  }, [damData]);

  const totalPnL = monthlyData.reduce((acc, row) => acc + row.total, 0);
  const avgDAM =
    monthlyData.reduce((acc, row) => acc + row.avgDam, 0) / monthlyData.length;

  // Στείλε το total στο parent component όταν αλλάζει
  useEffect(() => {
    if (onTotalChange && typeof onTotalChange === "function") {
      onTotalChange(totalPnL);
    }
  }, [totalPnL, onTotalChange]);

  return (
    <div className="container">
      {" "}
      <div className="title-with-tooltip">
        <h2>BL Monthly Settlement</h2> <TooltipInfoBox />
      </div>
      <div className="table-wrapper">
        <table>
          <thead>
            <tr>
              <th>Year</th>
              <th>Month</th>
              <th>Average DAM</th>
              <th>VBL PnL</th>
              {hasScroll && <td className="scroll-fix2" />}
            </tr>
          </thead>
        </table>

        <div className="scrollable-body" ref={scrollRef}>
          <table>
            <tbody>
              {monthlyData.map((row, i) => (
                <tr key={i}>
                  <td>{row.year}</td>
                  <td>{row.month}</td>
                  <td>{row.avgDam.toFixed(2)}</td>
                  <td
                    className={row.total >= 0 ? "green" : "red"}
                    style={{
                      backgroundColor: row.total >= 0 ? "#d9fdd3" : "#f8d7da",
                      fontWeight: 600,
                      textAlign: "center",
                      paddingRight: 8,
                    }}
                  >
                    {row.total.toLocaleString("el-GR", {
                      style: "currency",
                      currency: "EUR",
                    })}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="total-row-wrapper">
          <table>
            <tfoot>
              <tr>
                <td
                  colSpan={2}
                  style={{ textAlign: "center", fontWeight: "bold" }}
                >
                  Total
                </td>
                <td style={{ textAlign: "center", fontWeight: "bold" }}>
                  {avgDAM.toFixed(2)}
                </td>
                <td
                  className={totalPnL >= 0 ? "green" : "red"}
                  style={{
                    fontWeight: 600,
                    paddingRight: 9,
                    textAlign: "center",
                    backgroundColor: totalPnL >= 0 ? "#d9fdd3" : "#f8d7da",
                  }}
                >
                  {totalPnL.toLocaleString("el-GR", {
                    style: "currency",
                    currency: "EUR",
                  })}
                </td>
                {hasScroll && <td className="scroll-fix" />}
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  );
}
